import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ProposalDocumentsResponse,
  ProposalGuaranteeModalidade,
  ProposalManualRequirementPayload,
  ProposalRequirementPatch,
  createProposalManualRequirement,
  deleteProposalRequirementFile,
  getProposalDocuments,
  updateProposalRequirement,
  uploadProposalRequirementFile,
} from 'services/actions/ai_management_proposal_documents';
import {
  ProposalDocumentsState,
  ProposalRequirement,
  toProposalDocumentsState,
} from 'services/ai_management_proposal_documents';

interface Options {
  trackSave: <T>(request: Promise<T>) => Promise<T>;
}

export type RequirementBusy = 'upload' | 'confirm' | 'ignore' | 'reopen' | `remove-${string}`;

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status e pelo `erro` do
// corpo (esta etapa não usa `error`).
const errorOf = (res: { data: unknown; status?: number }, fallback: string): string | null => {
  const status = res?.status ?? 200;
  const body = res?.data as { erro?: string; error?: string } | undefined;
  if (status >= 400 || body?.erro || body?.error) return body?.erro || body?.error || fallback;
  return null;
};

interface WriteResult {
  block: ProposalDocumentsResponse | null;
  error: string | null;
}

const isBlock = (data: unknown): data is ProposalDocumentsResponse =>
  Boolean(data && typeof data === 'object' && 'declaracoes' in data && 'documentos' in data);

export function useProposalDocuments(biddingId: number | null, { trackSave }: Options) {
  const [state, setState] = useState<ProposalDocumentsState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<Record<string, RequirementBusy | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const load = useCallback(() => {
    if (!biddingId) return;
    const id = biddingId;
    setLoading(true);
    setLoadError(false);
    getProposalDocuments(id)
      .then((res) => {
        if (!mounted.current || biddingRef.current !== id) return;
        if (errorOf(res, 'erro') || !isBlock(res.data)) {
          setLoadError(true);
          return;
        }
        setState(toProposalDocumentsState(res.data));
      })
      .catch(() => {
        if (mounted.current && biddingRef.current === id) setLoadError(true);
      })
      .finally(() => {
        if (mounted.current && biddingRef.current === id) setLoading(false);
      });
  }, [biddingId]);

  useEffect(() => {
    setState(null);
    setBusy({});
    setErrors({});
    load();
  }, [load]);

  // Toda escrita devolve o bloco inteiro: a tela troca o estado pela resposta (sem otimismo).
  const write = useCallback(
    (
      key: string,
      action: RequirementBusy | null,
      request: () => Promise<{ data: unknown; status?: number }>,
      fallback: string,
    ): Promise<WriteResult> => {
      if (!biddingId) return Promise.resolve({ block: null, error: fallback });
      const id = biddingId;
      if (action) setBusy((prev) => ({ ...prev, [key]: action }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));

      return trackSave(request())
        .then((res) => {
          if (!mounted.current || biddingRef.current !== id) return { block: null, error: null };
          const message = errorOf(res, fallback);
          if (message || !isBlock(res.data)) {
            setErrors((prev) => ({ ...prev, [key]: message || fallback }));
            return { block: null, error: message || fallback };
          }
          setState(toProposalDocumentsState(res.data));
          return { block: res.data, error: null };
        })
        .catch(() => {
          if (mounted.current && biddingRef.current === id) {
            setErrors((prev) => ({ ...prev, [key]: fallback }));
          }
          return { block: null, error: fallback };
        })
        .finally(() => {
          if (action && mounted.current && biddingRef.current === id) {
            setBusy((prev) => ({ ...prev, [key]: undefined }));
          }
        });
    },
    [biddingId, trackSave],
  );

  const patch = useCallback(
    (
      item: ProposalRequirement,
      payload: ProposalRequirementPatch,
      action: RequirementBusy,
      fallback: string,
    ) =>
      write(
        item.id,
        action,
        () => updateProposalRequirement(biddingId as number, item.kind, item.id, payload),
        fallback,
      ),
    [biddingId, write],
  );

  const confirm = useCallback(
    (item: ProposalRequirement) =>
      patch(
        item,
        { confirmar: true },
        'confirm',
        {
          amostra: 'Não foi possível marcar a amostra como preparada.',
          declaracao: 'Não foi possível confirmar a declaração.',
        }[item.tipo as string] ?? 'Não foi possível confirmar o documento.',
      ),
    [patch],
  );

  const ignore = useCallback(
    (item: ProposalRequirement) =>
      patch(item, { ignorada: true }, 'ignore', 'Não foi possível ignorar a exigência.'),
    [patch],
  );

  // No back, `reabrir` só desfaz a confirmação; a exigência ignorada volta com `ignorada: false`.
  const reopen = useCallback(
    (item: ProposalRequirement) =>
      patch(
        item,
        item.status === 'ignorada' ? { ignorada: false } : { reabrir: true },
        'reopen',
        'Não foi possível reabrir a exigência.',
      ),
    [patch],
  );

  // Gravação automática do texto e dos campos: sem botão ocupado, só o "Salvando…" da casca.
  const updateDeclaration = useCallback(
    (
      item: ProposalRequirement,
      payload: Pick<
        ProposalRequirementPatch,
        'campos' | 'texto_final' | 'texto_origem' | 'modelo_id'
      >,
    ) =>
      write(
        item.id,
        null,
        () => updateProposalRequirement(biddingId as number, item.kind, item.id, payload),
        'Não foi possível salvar a declaração.',
      ),
    [biddingId, write],
  );

  const uploadFile = useCallback(
    (item: ProposalRequirement, file: File, modalidade?: ProposalGuaranteeModalidade) =>
      write(
        item.id,
        'upload',
        () =>
          uploadProposalRequirementFile(biddingId as number, item.kind, item.id, file, modalidade),
        'Não foi possível enviar o arquivo.',
      ),
    [biddingId, write],
  );

  const removeFile = useCallback(
    (item: ProposalRequirement, fileKey: string) =>
      write(
        item.id,
        `remove-${fileKey}`,
        () => deleteProposalRequirementFile(biddingId as number, item.kind, item.id, fileKey),
        'Não foi possível remover o arquivo.',
      ),
    [biddingId, write],
  );

  // O POST devolve o bloco inteiro: o item novo é o id que não existia antes.
  const addRequirement = useCallback(
    async (
      payload: ProposalManualRequirementPayload,
      file: File | null,
    ): Promise<string | null> => {
      const before = new Set((state?.requirements ?? []).map((item) => item.id));
      const { block, error } = await write(
        'add',
        null,
        () => createProposalManualRequirement(biddingId as number, payload),
        'Não foi possível adicionar a exigência.',
      );
      if (!block) return error;

      if (file && payload.tipo_exigencia === 'documento') {
        const created = block.documentos.find((item) => !before.has(item.id));
        if (created) {
          const sent = await write(
            created.id,
            'upload',
            () =>
              uploadProposalRequirementFile(biddingId as number, 'documentos', created.id, file),
            'A exigência foi adicionada, mas não foi possível enviar o arquivo.',
          );
          if (sent.error) return sent.error;
        }
      }
      return null;
    },
    [biddingId, state, write],
  );

  return {
    state,
    loading,
    loadError,
    reload: load,
    busy,
    errors,
    confirm,
    ignore,
    reopen,
    uploadFile,
    removeFile,
    addRequirement,
    updateDeclaration,
  };
}
